import { useEffect, useState } from "react";
import api, { apiErrorMessage, formatPLN, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Send } from "lucide-react";

const STATUS_LABEL = { oczekuje: "Oczekuje", zaakceptowane: "Zaakceptowane", odrzucone: "Odrzucone" };
const STATUS_CLASS = {
  oczekuje: "bg-amber-50 text-amber-700 border-amber-200",
  zaakceptowane: "bg-emerald-50 text-emerald-700 border-emerald-200",
  odrzucone: "bg-red-50 text-red-700 border-red-200",
};

export default function BudgetIncreaseRequestPage() {
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ requested_new_budget: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    const s = await api.get("/dashboard/summary");
    setSummary(s.data);
    const l = await api.get("/budget-increase-requests");
    setList(l.data);
  };

  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.requested_new_budget) <= (summary?.monthly_budget || 0)) {
      toast.error("Proponowany budżet musi być wyższy niż aktualny.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/budget-increase-requests", {
        requested_new_budget: Number(form.requested_new_budget),
        reason: form.reason,
      });
      toast.success("Wniosek wysłany do kierownika");
      setForm({ requested_new_budget: "", reason: "" });
      reload();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader testId="budget-request-page" title="Poproś o zwiększenie budżetu" subtitle="Wniosek trafi do kierownika budowy." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="ol-card p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Obecny budżet</p>
              <p className="text-xl font-bold text-slate-900 mt-1" data-testid="current-budget">{formatPLN(summary?.monthly_budget || 0)}</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Wykorzystano</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{formatPLN(summary?.used_budget_current_month || 0)}</p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Proponowany nowy budżet *</label>
            <input
              required type="number" min="0" step="0.01"
              value={form.requested_new_budget}
              onChange={(e) => setForm({ ...form, requested_new_budget: e.target.value })}
              data-testid="new-budget-input"
              placeholder="np. 15000"
              className="w-full px-3 py-2 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:ring-2 focus:ring-[#FF6B1A]/30 focus:outline-none text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Powód *</label>
            <textarea
              required rows={4}
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              data-testid="reason-input"
              placeholder="Opisz powód zwiększenia budżetu…"
              className="w-full px-3 py-2 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:ring-2 focus:ring-[#FF6B1A]/30 focus:outline-none text-sm"
            />
          </div>

          <button type="submit" disabled={submitting} data-testid="budget-submit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold transition-colors disabled:opacity-60">
            <Send className="w-4 h-4" /> Wyślij wniosek
          </button>
        </form>

        <div className="ol-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 className="text-base font-semibold text-slate-900">Moje wnioski</h3>
          </div>
          {list.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Brak wniosków</div>
          ) : (
            <table className="ol-table">
              <thead><tr><th>Data</th><th className="text-right">Obecny</th><th className="text-right">Proponowany</th><th>Status</th></tr></thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td className="text-xs text-slate-600">{formatDate(r.created_at)}</td>
                    <td className="text-right">{formatPLN(r.current_budget)}</td>
                    <td className="text-right font-semibold">{formatPLN(r.requested_new_budget)}</td>
                    <td><span className={`ol-badge border ${STATUS_CLASS[r.status]}`}>{STATUS_LABEL[r.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
